
interface IPServiceResponse {
  ip: string;
  source: "ipify" | "cloudflare" | "ip-api" | "cache";
  timestamp: number;
}

interface CachedIP {
  ip: string;
  source: string;
  timestamp: number;
}

const CACHE_KEY = "user_ip_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const getCachedIP = (): CachedIP | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedIP = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - data.timestamp < CACHE_DURATION) {
      return data;
    }

    // Cache expired, remove it
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch {
    return null;
  }
};

const setCachedIP = (ip: string, source: string): void => {
  try {
    const data: CachedIP = {
      ip,
      source,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors
  }
};

const fetchFromIpify = async (): Promise<string | null> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json", {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.ip || null;
  } catch {
    return null;
  }
};

const fetchFromCloudflare = async (): Promise<string | null> => {
  try {
    const response = await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
      method: "GET",
    });

    if (!response.ok) return null;

    const text = await response.text();
    const match = text.match(/ip=([^\n]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

const fetchFromIpApi = async (): Promise<string | null> => {
  try {
    const response = await fetch("http://ip-api.com/json/", {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data.query || null;
  } catch {
    return null;
  }
};

export const getUserIP = async (): Promise<IPServiceResponse> => {
  // Check cache first
  const cached = getCachedIP();
  if (cached) {
    return {
      ip: cached.ip,
      source: "cache",
      timestamp: cached.timestamp,
    };
  }

  // Try ipify first (most reliable)
  const ipifyResult = await fetchFromIpify();
  if (ipifyResult) {
    setCachedIP(ipifyResult, "ipify");
    return {
      ip: ipifyResult,
      source: "ipify",
      timestamp: Date.now(),
    };
  }

  // Try Cloudflare second (very fast)
  const cloudflareResult = await fetchFromCloudflare();
  if (cloudflareResult) {
    setCachedIP(cloudflareResult, "cloudflare");
    return {
      ip: cloudflareResult,
      source: "cloudflare",
      timestamp: Date.now(),
    };
  }

  // Try ip-api as fallback
  const ipApiResult = await fetchFromIpApi();
  if (ipApiResult) {
    setCachedIP(ipApiResult, "ip-api");
    return {
      ip: ipApiResult,
      source: "ip-api",
      timestamp: Date.now(),
    };
  }

  // All services failed, return fallback IP
  return {
    ip: "0.0.0.0",
    source: "cache",
    timestamp: Date.now(),
  };
};

export const clearIPCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Ignore localStorage errors
  }
};

export const getCachedIPSync = (): string | null => {
  const cached = getCachedIP();
  return cached ? cached.ip : null;
};
